/**
 * Web storage is not something we can assume exists. The SDKs run inside
 * third-party iframes, so they hit every browser storage restriction there is,
 * and they fail in three different ways that each need their own guard:
 *
 * 1. **The `window.localStorage` getter itself throws.** Safari and Firefox
 *    raise `SecurityError` (DOMException code 18) when storage is partitioned
 *    or cookies are blocked for a cross-site iframe. Merely evaluating
 *    `window.localStorage` in a truthiness check is enough to crash.
 * 2. **The property reads fine but the operations throw.** Firefox raises
 *    `NS_ERROR_FAILURE` on `getItem`/`setItem` when the site's storage is
 *    blocked, and Safari private mode raises `QuotaExceededError` on write.
 * 3. **Storage accepts writes but drops them.** Seen in some in-app browsers.
 *
 * That is why availability is proven with a real write/read/remove round trip
 * rather than a property check, and why every operation is still wrapped: the
 * probe can pass at startup and the storage can start failing later (quota).
 */

/**
 * Key used for the availability probe. Written and removed on every probe.
 *
 * Deliberately fixed rather than unique per run: if cleanup ever fails, a fixed
 * key leaves at most one stray entry that the next probe overwrites and clears,
 * whereas a unique key would orphan a new entry on every page load and creep
 * toward the quota the probe exists to check.
 */
const PROBE_KEY = "__ll_storage_probe__";

/**
 * Simple in-memory storage fallback for environments where localStorage is unavailable.
 * Stores data for the duration of the session only.
 */
class MemoryStorage implements Storage {
  private data: Map<string, string> = new Map();

  getItem(key: string): string | null {
    return this.data.get(key) ?? null;
  }

  setItem(key: string, value: string): void {
    this.data.set(key, value);
  }

  removeItem(key: string): void {
    this.data.delete(key);
  }

  clear(): void {
    this.data.clear();
  }

  key(index: number): string | null {
    const keys = Array.from(this.data.keys());
    return keys[index] ?? null;
  }

  get length(): number {
    return this.data.size;
  }
}

/**
 * Reads `window.localStorage` without ever throwing.
 *
 * The property access is the dangerous part (case 1 above), so it lives alone
 * inside its own try/catch.
 *
 * @returns The Storage object, or null when it cannot even be read
 */
function readLocalStorage(): Storage | null {
  try {
    if (typeof window === "undefined") {
      return null;
    }

    return window.localStorage ?? null;
  } catch (_error) {
    // SecurityError: storage access is blocked for this context
    return null;
  }
}

/**
 * Proves a storage backend actually works by round-tripping a value through it.
 *
 * @param storage The storage backend to probe
 * @returns Whether the value written could be read back
 */
function probeStorage(storage: Storage): boolean {
  try {
    storage.setItem(PROBE_KEY, PROBE_KEY);

    return storage.getItem(PROBE_KEY) === PROBE_KEY;
  } catch (_error) {
    // NS_ERROR_FAILURE / QuotaExceededError / anything else the browser throws
    return false;
  } finally {
    // Cleanup lives in `finally` so it also runs when the probe dies between
    // the write and the read, which would otherwise leave the key behind.
    //
    // The inner try/catch is not optional: an exception raised inside a
    // `finally` block replaces the pending return value and is *not* caught by
    // the sibling `catch`, so a throwing removeItem would escape this function
    // and reach the module-scope callers this file exists to protect.
    try {
      storage.removeItem(PROBE_KEY);
    } catch (_cleanupError) {
      // Storage is unusable, so there is nothing to clean up with.
    }
  }
}

/**
 * Memoized so we do not pay for a write/read/remove round trip on every
 * `BrowserStorage` instantiation. `undefined` means "not probed yet".
 */
let localStorageAvailable: boolean | undefined;

/**
 * Whether `window.localStorage` is readable *and* usable in this context.
 *
 * Use this before handing storage-backed configuration to a third party (e.g.
 * Mixpanel's `persistence` option) so it never has to discover the failure the
 * hard way.
 *
 * @returns Whether localStorage can be written to and read back
 */
export function isLocalStorageAvailable(): boolean {
  if (localStorageAvailable === undefined) {
    const storage = readLocalStorage();
    localStorageAvailable = storage ? probeStorage(storage) : false;
  }

  return localStorageAvailable;
}

/**
 * Clears the memoized availability result.
 *
 * Only intended for tests, which swap the `localStorage` global between cases.
 */
export function resetLocalStorageAvailability() {
  localStorageAvailable = undefined;
}

/**
 * Get or create the appropriate storage backend.
 * Uses localStorage if it is proven to work, falls back to in-memory storage.
 */
function getStorageBackend(): Storage {
  if (isLocalStorageAvailable()) {
    const storage = readLocalStorage();

    if (storage) {
      return storage;
    }
  }

  return new MemoryStorage();
}

/**
 * This class is named "Browser" and not "Local" on purpose in case
 * we decide to use something else than local storage.
 *
 * Every operation is best effort: storage that starts failing mid-session is
 * swapped for an in-memory backend rather than allowed to throw. Callers get
 * degraded persistence instead of an exception, because for every caller here
 * (session id, device id, commute history) losing the value is preferable to
 * taking down the render.
 */
export class BrowserStorage {
  protected group?: string;

  protected storage: Storage;

  constructor(group?: string) {
    this.group = group;
    this.storage = getStorageBackend();
  }

  /**
   * Sets the group under which to save a value.
   *
   * E.g. as a group of "global" and a key of "thing", the location
   * in storage would be "global.thing"
   *
   * @param group Unique key to group under
   */
  setGroup(group: string) {
    this.group = group;
  }

  /**
   * @returns Currently set group name
   */
  getGroup() {
    return this.group;
  }

  /**
   * Returns a value stored in storage
   *
   * @param key The key under the group "group.key"
   * @returns String value saved under "group.key", or null when unavailable
   */
  getItem(key: string) {
    try {
      return this.storage.getItem(this.getNamespacedKey(key));
    } catch (_error) {
      this.fallBackToMemory();

      return null;
    }
  }

  /**
   * Saves a new value in storage
   *
   * @param key The key under the group "group.key"
   * @param value String value to save
   */
  setItem(key: string, value: string) {
    try {
      this.storage.setItem(this.getNamespacedKey(key), value);
    } catch (_error) {
      this.fallBackToMemory();

      try {
        this.storage.setItem(this.getNamespacedKey(key), value);
      } catch (_retryError) {
        // Nothing left to try. The value is lost, which is acceptable.
      }
    }
  }

  /**
   * Removes a stored value
   *
   * @param key The key under the group "group.key"
   */
  removeItem(key: string) {
    try {
      this.storage.removeItem(this.getNamespacedKey(key));
    } catch (_error) {
      this.fallBackToMemory();
    }
  }

  /**
   * @param key The key under the group
   * @returns The key prefixed with the group, when one is set
   */
  protected getNamespacedKey(key: string) {
    return this.group ? `${this.group}.${key}` : key;
  }

  /**
   * Swaps a failing backend for an in-memory one so the rest of the session
   * keeps working. Also invalidates the memoized availability result so later
   * callers (and new instances) stop trying the broken backend.
   */
  protected fallBackToMemory() {
    if (this.storage instanceof MemoryStorage) {
      return;
    }

    localStorageAvailable = false;
    this.storage = new MemoryStorage();
  }
}

export default new BrowserStorage();
